import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Item } from '@/app/types/item';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MoreHorizontal, Pencil, Trash2, Tags } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTableColumnHeader } from '@/components/ui/data-table-column-header';
import { Decimal } from 'decimal.js';

// Custom event for editing an item
export const editItemEvent = 'edit-item';
// Custom event for deleting an item
export const deleteItemEvent = 'delete-item';
// Custom event for managing item prices
export const manageItemPricesEvent = 'manage-item-prices';

// Dispatch a custom event for item actions
const dispatchItemEvent = (item: Item, eventName: string): void => {
  const event = new CustomEvent(eventName, { detail: { item } });
  document.dispatchEvent(event);
};

// Component to display sale price
const SalePriceCell: React.FC<{ item: Item }> = ({ item }) => {
  // Directly calculate the price without state
  const displayPrice = item.sale_price 
    ? `₹${new Decimal(String(item.sale_price)).toFixed(2)}`
    : 'N/A';

  return <div>{displayPrice}</div>;
};

// Dynamic columns function that accepts categories
export const getColumns = (categories?: any[]): ColumnDef<Item>[] => [
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ row }) => <div className="font-medium">{row.getValue('name')}</div>,
    meta: {
      label: 'Name',
      placeholder: 'Filter by name...',
      variant: 'text',
    },
    enableSorting: true,
  },
  {
    accessorKey: 'code',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Code" />
    ),
    cell: ({ row }) => <div>{row.getValue('code') || '-'}</div>,
    meta: {
      label: 'Code',
      placeholder: 'Filter by code...',
      variant: 'text',
    },
    enableSorting: true,
  },
  {
    accessorKey: 'item_type',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Type" />
    ),
    cell: ({ row }) => {
      const itemType = row.getValue('item_type') as 'product' | 'service';
      return (
        <Badge variant={itemType === 'product' ? 'default' : 'secondary'}>
          {itemType === 'product' ? 'Product' : 'Service'}
        </Badge>
      );
    },
    meta: {
      label: 'Type',
      variant: 'select',
      options: [
        { label: 'Product', value: 'product' },
        { label: 'Service', value: 'service' },
      ],
    },
    enableSorting: true,
  },
  {
    accessorKey: 'category_id',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Category" />
    ),
    cell: ({ row }) => {
      const item = row.original;
      // Support both nested and flat structure
      return <div>{item.category?.name || item.category_name || '-'}</div>;
    },
    meta: {
      label: 'Category',
      variant: 'select',
      options: Array.isArray(categories) ? categories.map(cat => ({
        label: cat.name,
        value: cat.id.toString(),
      })) : [],
    },
    enableSorting: false,
    enableColumnFilter: true,
  },
  {
    accessorKey: 'sale_price',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Sale Price" />
    ),
    cell: ({ row }) => <SalePriceCell item={row.original} />,
    meta: {
      label: 'Sale Price',
      placeholder: 'Filter by price...',
      variant: 'number',
    },
    enableSorting: true,
  },
  {
    accessorKey: 'hsn_code',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="HSN Code" />
    ),
    cell: ({ row }) => <div>{row.getValue('hsn_code') || '-'}</div>,
    meta: {
      label: 'HSN Code',
      placeholder: 'Filter by HSN...',
      variant: 'text',
    },
    enableSorting: false,
  },
  {
    accessorKey: 'base_unit.name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Unit" />
    ),
    cell: ({ row }) => {
      const item = row.original;
      // Support both nested and flat structure
      return <div>{item.base_unit?.name || item.base_unit_name || '-'}</div>;
    },
    enableSorting: false,
  },
  {
    accessorKey: 'tax_rate.name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Tax Rate" />
    ),
    cell: ({ row }) => {
      const item = row.original;
      // Support both nested and flat structure
      return <div>{item.tax_rate?.name || item.tax_rate_name || (item.is_gst_applicable ? 'Not Set' : 'N/A')}</div>;
    },
    enableSorting: false,
  },
  {
    accessorKey: 'is_active',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const isActive = row.getValue('is_active') as boolean;
      return (
        <Badge variant={isActive ? 'default' : 'destructive'} className={isActive ? 'bg-green-500 hover:bg-green-600' : ''}>
          {isActive ? 'Active' : 'Inactive'}
        </Badge>
      );
    },
    meta: {
      label: 'Status',
      variant: 'select',
      options: [
        { label: 'Active', value: 'true' },
        { label: 'Inactive', value: 'false' },
      ],
    },
    enableSorting: false,
  },
  {
    accessorKey: 'created_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: ({ row }) => {
      const item = row.original;
      const date = item.created_at ? new Date(item.created_at) : null;
      return <div>{date ? date.toLocaleDateString() : '-'}</div>;
    },
    meta: {
      label: 'Created Date',
      variant: 'dateRange',
    },
    enableSorting: true,
    enableColumnFilter: true,
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const item = row.original;

      return (
        <div data-action-cell>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => dispatchItemEvent(item, manageItemPricesEvent)}
                className="cursor-pointer"
              >
                <Tags className="mr-2 h-4 w-4" />
                Manage Prices
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => dispatchItemEvent(item, editItemEvent)}
                className="cursor-pointer"
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit Details
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => dispatchItemEvent(item, deleteItemEvent)}
                className="cursor-pointer text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    },
  },
];

// Export default columns for backward compatibility (without categories)
export const columns = getColumns(); 